import { createAsyncThunk, createSlice, isPending } from "@reduxjs/toolkit";
import { addToMyPlansAsync, updatePlanAsync } from "components/features/MyPlanDetails/MyPlanDetails.slice";
import { uniq, uniqBy } from "lodash";
import {
  PAGE_SIZE,
  abovePropertyViewTypePlan,
  abovePropertyViewTypePlanTemplate,
  abovePropertyViewTypeTemplate
} from "utils/CommonConstants";
import { getHltInncodes } from "utils/CommonFunctions";
import { responseTypes, serviceTypes } from "utils/Types";
import { axiosGet, axiosPost, axiosPut } from "../../../utils/APIHelper";

import { deleteMyPlansAsync } from "../MyPlans/myplans.slice";
import { AbovePropertyState } from "./AboveProperty";

const filtersInitialState = {
  planViewFilters: {
    assignedPlanFilters: {
      functionalAreas: [],
      source: [],
      searchText: ""
    },
    customPlanFilters: {
      functionalAreas: [],
      searchText: ""
    },
    brandDeadlineFilters: {
      functionalAreas: [],
      searchText: ""
    },
    initiativeFilters: { functionalAreas: [], initiatives: [], searchText: "" },
    planProgressFilters: {
      source: [],
      roleCategory: [],
      inncode: [],
      owners: [],
      status: [],
      searchText: ""
    },
    unImplementedFilters: {
      inncode: [],
      searchText: ""
    }
  },
  individualPropertyFilters: {
    memberFilters: {
      searchText: "",
      role: [],
      roleCategory: [],
      engagements: [],
      includeAboveProperty: false
    },
    propertyPlans: {
      owners: [],
      status: [],
      searchText: "",
      source: [],
      functionalAreas: []
    },
    brandDeadlines: {
      searchText: "",
      functionalAreas: []
    },
    initiatives: {
      searchText: "",
      functionalAreas: []
    }
  },
  dialogFilters: {
    planAssignment: {
      searchText: "",
      role: [],
      property: [],
      includeAboveProperty: false
    },

    bookmarkProperty: {
      searchText: "",
      brandCodes: [],
      engagements: [],
      filteredInnCodes: []
    }
  },
  propertyView: {
    property: {
      searchText: "",
      brandCodes: [],
      engagements: []
    }
  },
  myPlans: {
    propertyPlans: {
      functionalAreas: [],
      status: [],
      source: [],
      brand: [],
      inncodes: [],
      searchText: ""
    },
    templatePlans: {
      functionalAreas: [],
      searchText: ""
    }
  },
  userView: {
    plans: {
      status: [],
      source: [],
      searchText: "",
      functionalAreas: []
    }
  }
};

export const initialState: AbovePropertyState = {
  owners: [],
  brands: [],
  members: [],
  allProperties: [],
  multiTargetDetails: [],
  planProgressMultiTarget: {},
  multiTargetTotalRecords: 0,
  totalRecords: 0,
  propertyList: [],
  myPlanDetails: {},
  aboveProperties: [],
  abovePropertiesDialog: [],
  roleCategoryList: [],
  initiativeFilterValues: [],
  bookmarkedIdList: [],
  bookmarkedTemplate: [],
  assignedLibraryPlan: [],
  bookmarkedProperties: [],
  abovePropertyLoader: false,
  userSnapshot: {
    USER_PROPERTY_CARDS_SNAPSHOT: [],
    USER_PROPERTY_PLAN_SNAPSHOT: []
  },
  propertySnapshot: {
    PROPERTY_CARDS_SNAPSHOT: [],
    PROPERTY_PLAN_SNAPSHOT: []
  },
  planProgress: {
    planDetail: {},
    createNewPlan: {},
    plansList: [],
    totalList: 0,
    unImplementedProperties: [],
    unImplemenetedCount: 0,
    owners: []
  },
  planProperty: {
    totalRecords: 0,
    planPropertyList: []
  },
  filters: filtersInitialState
};

function isRejectedAction(action: any) {
  return action.type.endsWith("rejected");
}

export const fetchAboveProperties = createAsyncThunk("fetchAboveProperties", async (data: any, thunkApi) => {
  const API_URL = `above-property/properties`;
  const innCodes = getHltInncodes();
  const response: any = await axiosPost(API_URL, serviceTypes.userplan, { innCodes, ...data });
  if (response?.status !== responseTypes.success) {
    return thunkApi.rejectWithValue(response.response.data);
  }
  return response.data;
});

export const fetchAbovePropertiesForDialog = createAsyncThunk(
  "fetchAbovePropertiesDialog",
  async (data: any, thunkApi) => {
    const API_URL = `above-property/properties`;
    const innCodes = getHltInncodes();
    const response: any = await axiosPost(API_URL, serviceTypes.userplan, { innCodes, ...data });
    if (response?.status !== responseTypes.success) {
      return thunkApi.rejectWithValue(response.response.data);
    }
    return response.data;
  }
);

export const fetchPlanDetailAsync = createAsyncThunk("aboveProperty/planDetails", async (data: any, thunkApi) => {
  const { rejectWithValue } = thunkApi;
  const { planId, innCode, innCodes } = data;

  const API_URL = innCodes
    ? `plans/${planId}?innCode=${innCode}&otherInnCodes=${innCodes}`
    : `plans/${planId}?innCode=${innCode}`;

  const response: any = await axiosGet(API_URL);

  if (response.status !== responseTypes.success) {
    return rejectWithValue(response.response.data);
  }

  return response.data;
});

export const fetchInitiativeFilterAsync = createAsyncThunk(
  "aboveProperty/initiativeFilter",
  async (data: any, thunkApi) => {
    const API_URL = `plans/initiative-filter?innCodes=${data}`;
    const response: any = await axiosGet(API_URL);
    if (response.status !== responseTypes.success) {
      return rejectWithValue(response.response.data);
    }
    return response.data;
  }
);

export const fetchAssignedLibraryPlansAsync = createAsyncThunk("assignedPlan", async (data: any, thunkApi) => {
  const API_URL = `above-property/assigned-plans`;
  const response: any = await axiosPost(API_URL, serviceTypes.userplan, data);

  if (response?.status !== responseTypes.success) {
    return thunkApi.rejectWithValue(response.response.data);
  }

  return response.data;
});

export const fetchBookmarkPropertyOrPlan = createAsyncThunk(
  "fetchBookmarkPropertyOrPlan",
  async (data: any, thunkApi) => {
    const API_URL = `above-property/bookmark?viewType=${data.viewType}`;
    const response: any = await axiosGet(API_URL, serviceTypes.userplan);
    if (response?.status !== responseTypes.success) {
      return thunkApi.rejectWithValue(response.response.data);
    }
    return response.data;
  }
);

export const updateBookmarkPropertyOrPlan = createAsyncThunk(
  "updateBookmarkPropertyOrPlan",
  async (data: any, thunkApi) => {
    const API_URL = `above-property/bookmark?viewType=${data.viewType}`;
    const response: any = await axiosPut(API_URL, serviceTypes.userplan, data?.payload);
    if (response?.status !== responseTypes.success) {
      return thunkApi.rejectWithValue(response.response.data);
    }
    return response.data;
  }
);

export const fetchAssigneeDetailsByHtlIdsAsync = createAsyncThunk(
  `aboveProperty/assigneeDetailsByHtlIds`,
  async (data: any, thunkApi) => {
    const { rejectWithValue } = thunkApi;
    const API_URL = `associates?size=${data.size}&hltIds=${data.value}`;
    const response: any = await axiosGet(API_URL, serviceTypes.user);
    if (response.status !== responseTypes.success) {
      return rejectWithValue(response.response.data);
    }
    return response.data;
  }
);
export const fetchAssigneeDetailsByInnCodesAsync = createAsyncThunk(
  `aboveProperty/assigneeDetailsByInnCodes`,
  async (data: any, thunkApi) => {
    const { rejectWithValue } = thunkApi;
    const API_URL = `associates`;
    const response: any = await axiosGet(API_URL, serviceTypes.user, { ...data });
    if (response.status !== responseTypes.success) {
      return rejectWithValue(response.response.data);
    }
    return response.data;
  }
);

export const fetchMyPlansAsync = createAsyncThunk(`aboveProperty/myplans`, async (data: any, thunkApi) => {
  const { page, payload } = data;
  const { rejectWithValue } = thunkApi;
  const API_URL = `above-property/plans?page=${page}&size=${PAGE_SIZE}`;
  const response: any = await axiosPost(API_URL, serviceTypes.userplan, payload);
  if (response.status !== responseTypes.success) {
    return rejectWithValue(response.response.data);
  }
  return response.data;
});

export const fetchPropertyInfo = createAsyncThunk(`aboveProperty/propertyInfo`, async (data: any, thunkApi) => {
  const { rejectWithValue } = thunkApi;
  const API_URL = `property-info?inncodes=${data?.innCodesValue}`;
  const response: any = await axiosGet(API_URL, "library");
  if (response.status !== responseTypes.success) {
    return rejectWithValue(response.response.data);
  }
  return response.data;
});

export const fetchMembers = createAsyncThunk(`fetchMembers`, async (data: any, thunkApi) => {
  const { rejectWithValue } = thunkApi;
  const API_URL = `above-property/members?innCode=${data.innCode}&searchedText=${data.searchedText || ""}&engagements=${
    data.engagements || ""
  }&roles=${data.roles || ""}&roleCategory=${data.roleCategory || ""}&excludeAbovePropertyUsers=${
    data.excludeAbovePropertyUsers
  }`;
  const response: any = await axiosGet(API_URL, serviceTypes.userplan);
  if (response.status !== responseTypes.success) {
    return rejectWithValue(response.response.data);
  }
  return response.data;
});

export const assignMultiplePlans = createAsyncThunk("aboveProperty/assignedPlan", async (data: any, thunkApi) => {
  const { inncode, isBulkAssignment } = data.url;
  const { rejectWithValue } = thunkApi;
  const API_URL = `above-property/multiple-assignment?innCode=${inncode}&isBulkAssignment=${isBulkAssignment || false}`;
  const response: any = await axiosPost(API_URL, serviceTypes.userplan, data.payLoadData);

  if (response?.status === responseTypes.accepted) {
    return response;
  } else if (response.status !== responseTypes.success) {
    return rejectWithValue(response.response.data);
  } else if (
    response?.response?.status >= responseTypes.internal_error &&
    response?.response?.status < responseTypes.other_error
  ) {
    return thunkApi.rejectWithValue(response.response);
  }
});
export const fetchPropertySnapshot = createAsyncThunk(`propertySnapshot`, async (data: any, thunkApi) => {
  const { rejectWithValue } = thunkApi;
  const API_URL = `above-property/property/snapshot?innCode=${data?.inncode}`;
  const response: any = await axiosGet(API_URL, serviceTypes.userplan);
  if (response.status !== responseTypes.success) {
    return rejectWithValue(response.response.data);
  }
  return response.data;
});
export const fetchMultiTargetDetails = createAsyncThunk(`fetchMultiTargetDetails`, async (data: any, thunkApi) => {
  const { rejectWithValue } = thunkApi;
  const API_URL = `plans/multi-target`;
  const response: any = await axiosPost(API_URL, serviceTypes.library, data);
  if (response.status !== responseTypes.success) {
    return rejectWithValue(response.response);
  }
  return response.data;
});

export const fetchPlanProgressMultiTargetDetails = createAsyncThunk(
  `fetchPlanMultiTargetDetails`,
  async (data: any, thunkApi) => {
    const { rejectWithValue } = thunkApi;
    const API_URL = `plans/multi-target`;
    const response: any = await axiosPost(API_URL, serviceTypes.library, data);
    if (response.status !== responseTypes.success) {
      return rejectWithValue(response.response);
    }
    return response.data;
  }
);

export const fetchUnImplementedProperties = createAsyncThunk(
  `above-property/unimplemented-properties`,
  async (data: any, thunkApi) => {
    const { rejectWithValue } = thunkApi;
    const API_URL = `above-property/unimplemented-properties`;
    const response: any = await axiosGet(API_URL, serviceTypes.userplan, data);
    if (response.status !== responseTypes.success) {
      return rejectWithValue(response.response.data);
    }
    return response.data;
  }
);

export const fetchUserViewSnapshot = createAsyncThunk(`userViewSnapshot`, async (data: any, thunkApi) => {
  const { rejectWithValue } = thunkApi;
  const API_URL = `above-property/user-property/snapshot?externalHltId=${data?.hltId}&innCode=${data.innCode}`;
  const response: any = await axiosGet(API_URL, serviceTypes.userplan);
  if (response.status !== responseTypes.success) {
    return rejectWithValue(response.response.data);
  }
  return response.data;
});

export const AbovePropertySlice = createSlice({
  name: "above property",
  initialState,
  reducers: {
    clearPlanProgress: (state) => {
      state.planProgress = {
        planDetail: {},
        plansList: [],
        totalList: 0
      };
      state.planProgressMultiTarget = {};
    },

    setAbovePropertyFilters: (state, action) => {
      let parentView = { ...state.filters[action?.payload?.parentView] };
      let selectedFilterCatagory = parentView[action?.payload?.filterType];
      selectedFilterCatagory[action?.payload?.filterName] = action.payload?.value;
      state.filters[action?.payload?.parentView][action?.payload?.filterType] = selectedFilterCatagory;
    },

    setSearchText: (state, action) => {
      let parentView = { ...state.filters[action?.payload?.parentView] };
      let selectedFilterCatagory = parentView[action?.payload?.searchType];
      selectedFilterCatagory[action?.payload?.filterName] = action?.payload?.inputText || "";
      state.filters[action?.payload?.parentView][action?.payload?.filterType] = selectedFilterCatagory;
    },

    clearFilter: (state, action) => {
      let parentView = { ...state.filters[action?.payload?.parentView] };
      let selectedFilterCatagory = parentView[action?.payload?.filterType];
      Object.keys(selectedFilterCatagory).forEach((key) => {
        if (Array.isArray(selectedFilterCatagory[key])) selectedFilterCatagory[key] = [];
        else if (typeof selectedFilterCatagory[key] === "boolean" && !action?.payload?.isClearAll)
          selectedFilterCatagory[key] = false;
      });
      state.filters[action?.payload?.parentView][action?.payload?.filterType] = selectedFilterCatagory;
    },

    clearChildFilters: (state) => {
      let planViewFilters = state.filters.planViewFilters;
      let propertyViewFilters = state.filters.propertyView;
      state.filters = filtersInitialState;
      state.filters = {
        ...state.filters,
        planViewFilters: {
          ...planViewFilters,
          planProgressFilters: filtersInitialState.planViewFilters.planProgressFilters
        },
        propertyView: {
          ...filtersInitialState.propertyView,
          ...propertyViewFilters
        }
      };
    },

    resetAllFilters: (state) => {
      state.filters = filtersInitialState;
    },

    setBrands: (state, action) => {
      state.brands = action.payload;
    },

    setAllProperties: (state, action) => {
      state.allProperties = action.payload;
    },

    setSelectedMultiTarget: (state, action) => {
      state.planProgressMultiTarget = state.multiTargetDetails?.filter((item: any) => item?.id === action?.payload)[0];
    },

    setLoader: (state, action) => {
      state.abovePropertyLoader = action.payload;
    },

    setOwners: (state, action) => {
      state.owners = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder

      .addCase(fetchUserViewSnapshot.fulfilled, (state, action) => {
        state.abovePropertyLoader = false;
        state.userSnapshot = action?.payload;
      })
      .addCase(fetchPropertySnapshot.fulfilled, (state, action) => {
        state.abovePropertyLoader = false;
        state.propertySnapshot = action?.payload;
      })
      .addCase(fetchAboveProperties.fulfilled, (state, action) => {
        state.abovePropertyLoader = false;
        state.aboveProperties = action?.payload?.properties?.filter((a: any) => a?.brandCode);
      })
      .addCase(fetchAbovePropertiesForDialog.fulfilled, (state, action) => {
        state.abovePropertyLoader = false;
        state.abovePropertiesDialog = action?.payload?.properties?.filter((a: any) => a?.brandCode);
      })
      .addCase(fetchMembers.fulfilled, (state, action: any) => {
        state.members = action.payload.members;
        state.abovePropertyLoader = false;
      })
      .addCase(fetchMembers.rejected, (state) => {
        state.members = [];
        state.abovePropertyLoader = false;
      })
      .addCase(fetchInitiativeFilterAsync.fulfilled, (state, action: any) => {
        state.initiativeFilterValues = action.payload?.map((item: any) => {
          return { name: item };
        });
      })
      .addCase(fetchAssignedLibraryPlansAsync.pending, (state, action: any) => {
        state.abovePropertyLoader = action.meta.arg.pageable.page === 0;
      })
      .addCase(fetchAssignedLibraryPlansAsync.fulfilled, (state, action: any) => {
        state.abovePropertyLoader = false;
        state.assignedLibraryPlan =
          action.meta.arg.pageable.page === 0
            ? action.payload.plans
            : [...state.assignedLibraryPlan, ...action.payload.plans];
        state.totalRecords = action.payload?.meta?.page?.total;
      })
      .addCase(fetchBookmarkPropertyOrPlan.fulfilled, (state, action: any) => {
        state.abovePropertyLoader = false;
        if (action?.meta?.arg?.viewType === abovePropertyViewTypePlanTemplate) {
          state.bookmarkedIdList = action.payload?.bookmarkPlans;
          state.bookmarkedTemplate = action.payload?.bookmarkTemplatePlans;
        } else {
          state.bookmarkedProperties = action.payload?.bookmarkProperties;
        }
      })
      .addCase(updateBookmarkPropertyOrPlan.pending, (state, action: any) => {
        state.abovePropertyLoader = true;
      })
      .addCase(updateBookmarkPropertyOrPlan.fulfilled, (state, action: any) => {
        state.abovePropertyLoader = false;
        if (action?.meta?.arg?.viewType === abovePropertyViewTypePlan)
          state.bookmarkedIdList = action.payload?.bookmarkPlans;
        else if (action?.meta?.arg?.viewType === abovePropertyViewTypeTemplate)
          state.bookmarkedTemplate = action.payload?.bookmarkTemplatePlans;
        else state.bookmarkedProperties = action.payload?.bookmarkProperties;
      })
      .addCase(updateBookmarkPropertyOrPlan.rejected, (state) => {
        state.abovePropertyLoader = false;
      })
      .addCase(fetchPlanDetailAsync.fulfilled, (state, action: any) => {
        state.planProgress.planDetail = action.payload;
      })

      .addCase(fetchMyPlansAsync.fulfilled, (state, action: any) => {
        state.planProgress.meta = action.payload.meta;
        state.planProgress.plansList =
          action.meta.arg.page === 0
            ? action.payload.userplans
            : [...state.planProgress.plansList, ...action.payload.userplans];
        state.planProgress.totalList = action.payload?.meta?.page?.total;
        state.planProgress.owners = action.payload.meta.owners;
        state.planProgress.inncodes = uniq(action.payload.meta?.filters.map((item: any) => item.innCode));
        state.brands = uniqBy(action.payload.meta?.filters, "brandCode");
      })
      .addCase(fetchPropertyInfo.fulfilled, (state, action: any) => {
        state.propertyList = action.payload;
      })
      .addCase(fetchAssigneeDetailsByHtlIdsAsync.fulfilled, (state, action) => {
        state.roleCategoryList = action.payload.users;
      })
      .addCase(fetchAssigneeDetailsByInnCodesAsync.fulfilled, (state, action: any) => {
        state.planProperty.totalRecords = action.payload.meta.page.total;
        state.planProperty.planPropertyList =
          action.meta.arg.page === 0
            ? action.payload.users
            : [...state.planProperty.planPropertyList, ...action.payload.users];
        state.abovePropertyLoader = false;
      })
      .addCase(assignMultiplePlans.fulfilled, (state, action: any) => {
        state.abovePropertyLoader = false;
      })
      .addCase(fetchMultiTargetDetails.pending, (state, action: any) => {
        state.abovePropertyLoader = action.meta?.arg?.pagination?.page === 1;
      })
      .addCase(fetchMultiTargetDetails.fulfilled, (state, action: any) => {
        state.abovePropertyLoader = false;
        state.multiTargetDetails =
          action.meta?.arg?.pagination?.page === 1
            ? action.payload.plans
            : [...state.multiTargetDetails, ...action.payload.plans];
        state.multiTargetTotalRecords = action.payload?.meta?.page?.total;
      })
      .addCase(fetchPlanProgressMultiTargetDetails.pending, (state, action: any) => {
        state.abovePropertyLoader = action.meta?.arg?.pagination?.page === 1;
      })
      .addCase(fetchPlanProgressMultiTargetDetails.fulfilled, (state, action: any) => {
        state.planProgressMultiTarget =
          action.meta?.arg?.pagination?.page === 1
            ? action.payload.plans[0]
            : [...state.multiTargetDetails, ...action.payload.plans];
        state.multiTargetTotalRecords = action.payload?.meta?.page?.total;
      })
      .addCase(fetchUnImplementedProperties.fulfilled, (state, action: any) => {
        state.planProgress.unImplementedProperties =
          action.meta.arg.page === 0
            ? action.payload.userplans
            : [...state.planProgress.unImplementedProperties, ...action.payload.userplans];
        state.planProgress.unImplemenetedCount = action.payload?.meta?.page?.total;
        state.abovePropertyLoader = false;
      })
      .addCase(deleteMyPlansAsync.fulfilled, (state, action: any) => {
        state.planProgress.plansList = state.planProgress.plansList.filter(
          (plan: any) => plan.id !== action?.meta?.arg?.id
        );
        state.planProgress.totalList = state.planProgress.totalList - 1;
      })
      .addCase(addToMyPlansAsync.fulfilled, (state, action: any) => {
        if (action?.meta?.arg?.shouldDataRefresh) state.planProgress.createNewPlan = action.payload;
      })
      .addCase(updatePlanAsync.fulfilled, (state, action: any) => {
        state.planProgress.plansList = state.planProgress.plansList.map((element: any) => {
          if (element.id === action.payload.id) {
            element = { ...element, ...action.payload, title: action.payload.name };
          }
          return element;
        });
      })
      .addMatcher(
        isPending(
          fetchPropertySnapshot,
          fetchAboveProperties,
          fetchMembers,
          fetchBookmarkPropertyOrPlan,
          fetchPlanDetailAsync,
          assignMultiplePlans,
          fetchAbovePropertiesForDialog,
          fetchUserViewSnapshot
        ),
        (state) => {
          state.abovePropertyLoader = true;
        }
      )
      .addMatcher(
        isPending(fetchMyPlansAsync, fetchAssigneeDetailsByInnCodesAsync, fetchUnImplementedProperties),
        (state, action) => {
          state.abovePropertyLoader = action.meta.arg.page === 0;
        }
      )
      .addMatcher(isRejectedAction, (state) => {
        state.abovePropertyLoader = false;
      });
  }
});

export const {
  clearPlanProgress,
  setBrands,
  setAllProperties,
  clearFilter,
  resetAllFilters,
  setSearchText,
  setAbovePropertyFilters,
  setSelectedMultiTarget,
  setLoader,
  setOwners,
  clearChildFilters
} = AbovePropertySlice.actions;

export default AbovePropertySlice.reducer;
function rejectWithValue(data: any): any {
  throw new Error("Function not implemented.");
}
